/**
 * CoachEgoNetwork - Main container component
 *
 * Replaces the Insights tab content in Dashboard.js.
 * Renders an ego network visualization (left) and detail panel (right).
 */

import React, { useState, useCallback } from 'react';
import { Box, Alert, AlertTitle, Button, CircularProgress, Typography } from '@mui/material';
import EgoNetworkCanvas from './components/NetworkVisualization/EgoNetworkCanvas';
import DetailPanel from './components/DetailPanel/DetailPanel';
import ProgramTimeline from './components/Timeline/ProgramTimeline';
import useEgoNetwork from './hooks/useEgoNetwork';
import { DETAIL_PANEL_WIDTH, MESSAGES } from './constants/dashboardConfig';

 
 
 
 
 
 
 
 
// eslint-disable-next-line max-lines-per-function
function CoachEgoNetwork({ entityId, dateRange }) {
  // Selection state
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [vizMode, setVizMode] = useState('concentric');

  // Fetch ego network data
  const {
    nodes,
    edges,
    egoId,
    networkStats,
    loading,
    error,
    retry,
  } = useEgoNetwork(entityId, dateRange);

  // Selection handlers
  const handleNodeSelect = useCallback((node) => {
    setSelectedEdge(null);
    setSelectedNode((prev) => (prev?.id === node.id ? null : node));
  }, []);

  const handleEdgeSelect = useCallback((edge) => {
    setSelectedNode(null);
    setSelectedEdge((prev) =>
      prev?.source === edge.source && prev?.target === edge.target ? null : edge
    );
  }, []);

  const handleDeselect = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // No entity selected
  if (!entityId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="info">
          <AlertTitle>No Entity Selected</AlertTitle>
          {MESSAGES.noEntity}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            {MESSAGES.loading}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error</AlertTitle>
          {MESSAGES.error} {error.message || ''}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (!nodes || nodes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="info">
          <AlertTitle>No Network Data</AlertTitle>
          {MESSAGES.empty}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `1fr ${DETAIL_PANEL_WIDTH}px`,
        gridTemplateRows: '1fr auto',
        height: 'calc(100vh - 280px)',
        minHeight: 500,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Network Visualization Area */}
      <Box
        sx={{
          gridColumn: 1,
          gridRow: '1 / 3',
          position: 'relative',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)',
          overflow: 'hidden',
        }}
      >
        <EgoNetworkCanvas
          nodes={nodes}
          edges={edges}
          egoId={egoId}
          networkStats={networkStats}
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          vizMode={vizMode}
          onVizModeChange={setVizMode}
          onNodeSelect={handleNodeSelect}
          onEdgeSelect={handleEdgeSelect}
          onDeselect={handleDeselect}
        />

        {/* Timeline at bottom of network area */}
        <ProgramTimeline entityId={entityId} />
      </Box>

      {/* Detail Panel (Right Side) */}
      <Box
        sx={{
          gridColumn: 2,
          gridRow: '1 / 3',
          borderLeft: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <DetailPanel
          selectedNode={selectedNode}
          selectedEdge={selectedEdge}
          entityId={entityId}
          nodes={nodes}
          egoId={egoId}
          networkStats={networkStats}
          onClose={handleDeselect}
        />
      </Box>
    </Box>
  );
}

export default CoachEgoNetwork;
